import { DocumentSubtitleSegmentV1 } from '@utils/types/editors/subtitles/subtitle.ts';

/**
 * Pair a translated caption row with its originating source segment.
 *
 * The backend's `split_multiple_lines` can split one source segment into
 * two translated rows when the translated text runs 3+ lines (see
 * `api-scriptix/zoomlib/integrations/translation_service.py`). That
 * leaves `translation.length >= source.length` and breaks index pairing
 * from the split point onward — the symptom in #2250's reopen comment
 * (English 32 / Dutch 35, rows 3+ aligned to the wrong source line).
 *
 * The invariant we can lean on: every translated segment's [start, stop]
 * lies inside exactly one source segment's [start, stop]. When one
 * source maps to two translations, the first keeps `source.start → mid`
 * and the second keeps `mid → source.stop`, so matching by the
 * translation's center-point is unambiguous. Binary search keeps this
 * O(log n) per row for the 9k-cue docs that already hit this editor.
 */
export function findSourceSegment(
  sourceSegments: DocumentSubtitleSegmentV1[] | undefined,
  translation: DocumentSubtitleSegmentV1
): DocumentSubtitleSegmentV1 | undefined {
  if (!sourceSegments || sourceSegments.length === 0) return undefined;

  const center = (translation.start + translation.stop) / 2;

  let lo = 0;
  let hi = sourceSegments.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const seg = sourceSegments[mid];
    if (center < seg.start) {
      hi = mid - 1;
    } else if (center > seg.stop) {
      lo = mid + 1;
    } else {
      return seg;
    }
  }

  // No containing range — the segment was retimed outside every source
  // slot (manual edit after translation). Fall back to the nearest
  // source by start-time so the row still shows a sensible neighbour.
  const insertAt = lo;
  const before = sourceSegments[insertAt - 1];
  const after = sourceSegments[insertAt];
  if (!before) return after;
  if (!after) return before;
  const distBefore = Math.abs(center - before.stop);
  const distAfter = Math.abs(after.start - center);
  return distBefore <= distAfter ? before : after;
}

/**
 * Pixel height of one caption row: the 92px editor plus its 1px bottom
 * border. Both split panes use it so a source row padded to N slots is
 * exactly as tall as N stacked translated rows, which keeps the two
 * scroll-synced lists level.
 */
export const CAPTION_ROW_HEIGHT = 93;

export interface CaptionRowGroup {
  /** The source segment for this row; undefined only when there is no source. */
  source?: DocumentSubtitleSegmentV1;
  /** Translated segments that fall inside `source`, with their own index. */
  translations: { segment: DocumentSubtitleSegmentV1; index: number }[];
}

/**
 * One split-view row per source segment, holding every translated segment
 * that pairs to it (#2250). A source split in two by the backend gets both
 * halves in its row; a source with no translation gets an empty row. Row N
 * is always source segment N, so the source pane keeps its own indexes.
 */
export function groupTranslationsBySource(
  sourceSegments: DocumentSubtitleSegmentV1[] | undefined,
  translationSegments: DocumentSubtitleSegmentV1[]
): CaptionRowGroup[] {
  if (!sourceSegments || sourceSegments.length === 0) {
    return translationSegments.map((segment, index) => ({
      translations: [{ segment, index }]
    }));
  }
  const groups: CaptionRowGroup[] = sourceSegments.map(source => ({
    source,
    translations: []
  }));
  const indexOfSource = new Map(sourceSegments.map((s, i) => [s, i]));
  translationSegments.forEach((segment, index) => {
    const source = findSourceSegment(sourceSegments, segment);
    const row = source ? indexOfSource.get(source) : undefined;
    if (row !== undefined) groups[row].translations.push({ segment, index });
  });
  return groups;
}

/** Slots each source row must fill: at least one, else its translation count. */
export function rowSpans(groups: CaptionRowGroup[]): number[] {
  return groups.map(g => Math.max(1, g.translations.length));
}
